import React, {
  useContext, useEffect, useRef, useState,
} from 'react';
import {
  collection, query, where, onSnapshot,
  updateDoc, doc, getDoc, getCountFromServer,
} from 'firebase/firestore';
import {
  FiArchive, FiUserCheck, FiUsers, FiHelpCircle,
  FiChevronDown, FiSearch,
} from 'react-icons/fi';
import { toast }         from 'react-toastify';

import { db }            from '../utils/firebaseConfig';
import { AuthContext }   from '../contexts/AuthContext';
import ConfirmationModal from './ConfirmationModal';
import { logUserActivity } from '../utils/auditLogger';

/* ----------------------------------------------------------------- */
/* helpers ---------------------------------------------------------- */
const PillButton = ({
  children, onClick, colour = 'blue', aria,
}) => {
  const hues = {
    blue:  ['bg-blue-600',  'hover:bg-blue-700'],
    red:   ['bg-red-600',   'hover:bg-red-700'],
    green: ['bg-green-600', 'hover:bg-green-700'],
  };
  const [bg, hover] = hues[colour] || hues.blue;
  return (
    <button
      onClick={onClick}
      aria-label={aria}
      className={`${bg} ${hover} text-white text-xs sm:text-sm font-medium rounded-full px-4 py-2 shadow 
                  focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-${colour}-400
                  transition`}
    >
      {children}
    </button>
  );
};

/* ----------------------------------------------------------------- */
export default function ArchiveTrainees() {
  const { role, currentUser, userName } = useContext(AuthContext);

  const [active,   setActive]   = useState([]);
  const [archived, setArchived] = useState([]);
  const [activeCount, setActiveCount] = useState(0);
  const [archivedCount, setArchivedCount] = useState(0);
  const [search,   setSearch]   = useState('');
  const [showGuide, setGuide]   = useState(false);
  const [open, setOpen]         = useState({ active: false, archived: false });

  /* confirm modal state */
  const [confirm, setConfirm] = useState({
    isOpen: false, title: '', message: '', onConfirm: () => {},
  });
  const modalRef = useRef(null);

  /* ------------------- firestore listeners & count fetching ------------------------ */
  useEffect(() => {
    if (role !== 'admin') return;

    const fetchCounts = async () => {
      try {
        const activeQuery = query(
          collection(db, 'users'),
          where('role', '==', 'trainee'),
          where('archived', '==', false),
          where('active', '==', true)
        );
        const activeSnapshot = await getCountFromServer(activeQuery);
        setActiveCount(activeSnapshot.data().count);

        const archivedQuery = query(
          collection(db, 'users'),
          where('role', '==', 'trainee'),
          where('archived', '==', true),
          where('active', '==', true)
        );
        const archivedSnapshot = await getCountFromServer(archivedQuery);
        setArchivedCount(archivedSnapshot.data().count);

      } catch (error) {
        console.error("Error fetching trainee counts:", error);
        toast.error("Failed to load trainee counts.");
        setActiveCount(0);
        setArchivedCount(0);
      }
    };

    fetchCounts();
  }, [role]);

  useEffect(() => {
    let unsubscribe = null;
    if (role === 'admin' && open.active) {
      const qActive = query(
        collection(db, 'users'),
        where('role', '==', 'trainee'),
        where('archived', '==', false),
        where('active', '==', true)
      );
      unsubscribe = onSnapshot(qActive, (querySnapshot) => {
        setActive(querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      }, (error) => {
        console.error("Error fetching active trainees:", error);
        toast.error("Failed to load active trainees.");
        setActive([]); // Clear data on error
      });
    } else {
      setActive([]); // Clear data when not fetching or section is closed
    }

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [role, open.active]);

  useEffect(() => {
    let unsubscribe = null;
    if (role === 'admin' && open.archived) {
      const qArchived = query(
        collection(db, 'users'),
        where('role', '==', 'trainee'),
        where('archived', '==', true),
        where('active', '==', true)
      );
      unsubscribe = onSnapshot(qArchived, (querySnapshot) => {
        setArchived(querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      }, (error) => {
        console.error("Error fetching archived trainees:", error);
        toast.error("Failed to load archived trainees.");
        setArchived([]); // Clear data on error
      });
    } else {
      setArchived([]); // Clear data when not fetching or section is closed
    }

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [role, open.archived]);

  /* ------------------- archive / unarchive ----------------------- */
  const doArchiveToggle = (trainee, arch) => {
    setConfirm({
      isOpen: true,
      title: arch ? 'Archive Trainee' : 'Unarchive Trainee',
      message: `Are you sure you want to ${arch ? 'archive' : 'unarchive'} trainee ${trainee.name}?`,
      onConfirm: async () => {
        try {
          const ref  = doc(db, 'users', trainee.id);
          const snap = await getDoc(ref);
          if (!snap.exists()) { toast.error('Trainee does not exist'); return; }
          if (!snap.data().active) { toast.error('Cannot modify a deactivated trainee'); return; }

          await updateDoc(ref, { archived: arch });
          toast.success(`Trainee ${arch ? 'archived' : 'unarchived'} successfully`);

          await logUserActivity(
            currentUser?.uid || 'unknown_uid',
            userName || currentUser?.displayName || currentUser?.email || 'Unknown User',
            arch ? 'ARCHIVE_TRAINEE' : 'UNARCHIVE_TRAINEE',
            { traineeId: trainee.id, traineeName: trainee.name },
          );
        } catch (e) { console.error(e); toast.error('Operation failed'); }
      },
    });

    /* focus modal */
    setTimeout(() => {
      modalRef.current?.scrollIntoView({ behaviour: 'smooth', block: 'center' });
    }, 150);
  };

  const closeModal = () => setConfirm(s => ({ ...s, isOpen: false }));

  /* ------------------- derived lists ----------------------------- */
  const filter = list => list.filter(t =>
    t.name.toLowerCase().includes(search.toLowerCase())
    || t.email.toLowerCase().includes(search.toLowerCase()),
  );

  if (role !== 'admin') return <p className="text-center mt-12">Access Denied</p>;

  /* ------------------- styles ------------------------------------ */
  const card = 'rounded-xl shadow-xl bg-primary-light dark:bg-primary-dark border border-secondary-light dark:border-secondary-dark p-6 transition';
  const sectionHdr = 'flex items-center justify-between cursor-pointer select-none';
  const listGrid = 'grid gap-4 mt-4 sm:grid-cols-2';
  const rowBase = 'flex flex-col sm:flex-row sm:items-center justify-between bg-secondary-light dark:bg-secondary-dark rounded-lg p-4 shadow';

  /* ------------------- render ------------------------------------ */
  return (
    <div className={card}>
      {/* header */}
      <header className="flex items-center justify-between mb-6">
        <h1 className="text-xl sm:text-2xl font-bold flex items-center gap-2">
          <FiUsers className="shrink-0" /> Archive User Records
        </h1>
        <button
          onClick={() => setGuide(s => !s)}
          aria-label="toggle guide"
          className="text-text-light dark:text-text-dark hover:text-accent-light dark:hover:text-accent-dark transition"
        >
          <FiHelpCircle size={20} />
        </button>
      </header>

      {/* guide */}
      {showGuide && (
        <div className="mb-6 p-4 bg-secondary-light/50 dark:bg-secondary-dark/50 rounded-lg text-sm leading-relaxed">
          <p><strong>Archive User Records</strong> lets administrators move
          trainees in and out of the archive. An archived trainee can't log in
          until they're unarchived.</p>
        </div>
      )}

      {/* search */}
      <div className="relative mb-8">
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search by name or e‑mail…"
          className="w-full rounded-md bg-secondary-light dark:bg-secondary-dark border border-secondary-light dark:border-secondary-dark py-3 pl-12 pr-4
                     focus:outline-none focus:ring-2 focus:ring-accent-light dark:focus:ring-accent-dark transition"
        />
        <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-text-light dark:text-text-dark" />
      </div>

      {/* section – active */}
      <section className="mb-10">
        <div
          onClick={() => setOpen(o => ({ ...o, active: !o.active }))}
          className={sectionHdr}
        >
          <h2 className="text-lg font-semibold flex items-center gap-2">
            Active Trainees
            <span className="text-xs bg-accent-light dark:bg-accent-dark text-primary-light dark:text-primary-dark px-2 py-0.5 rounded-full">
              {activeCount}
            </span>
          </h2>
          <FiChevronDown
            size={22}
            className={`transition-transform ${open.active ? 'rotate-0' : '-rotate-90'}`}
          />
        </div>

        {open.active && (
          filter(active).length
            ? (
              <div className={listGrid}>
                {filter(active).map(t => (
                  <div key={t.id} className={rowBase}>
                    <div className="mb-2 sm:mb-0">
                      <p className="font-medium">{t.name}</p>
                      <p className="text-sm opacity-80">{t.email}</p>
                    </div>
                    <PillButton
                      colour="red"
                      onClick={() => doArchiveToggle(t, true)}
                      aria="archive trainee"
                    >
                      <FiArchive className="inline mr-1 -mt-0.5" /> Archive
                    </PillButton>
                  </div>
                ))}
              </div>
            )
            : <p className="mt-4 text-sm opacity-70">No active trainees.</p>
        )}
      </section>

      {/* section – archived */}
      <section>
        <div
          onClick={() => setOpen(o => ({ ...o, archived: !o.archived }))}
          className={sectionHdr}
        >
          <h2 className="text-lg font-semibold flex items-center gap-2">
            Archived Trainees
            <span className="text-xs bg-secondary-light dark:bg-secondary-dark px-2 py-0.5 rounded-full">
              {archivedCount}
            </span>
          </h2>
          <FiChevronDown
            size={22}
            className={`transition-transform ${open.archived ? 'rotate-0' : '-rotate-90'}`}
          />
        </div>

        {open.archived && (
          filter(archived).length
            ? (
              <div className={listGrid}>
                {filter(archived).map(t => (
                  <div key={t.id} className={rowBase}>
                    <div className="mb-2 sm:mb-0">
                      <p className="font-medium">{t.name}</p>
                      <p className="text-sm opacity-80">{t.email}</p>
                    </div>
                    <PillButton
                      colour="green"
                      onClick={() => doArchiveToggle(t, false)}
                      aria="unarchive trainee"
                    >
                      <FiUserCheck className="inline mr-1 -mt-0.5" /> Unarchive
                    </PillButton>
                  </div>
                ))}
              </div>
            )
            : <p className="mt-4 text-sm opacity-70">No archived trainees.</p>
        )}
      </section>

      {/* confirmation modal */}
      <ConfirmationModal
        ref={modalRef}
        isOpen={confirm.isOpen}
        title={confirm.title}
        message={confirm.message}
        onClose={closeModal}
        onConfirm={() => { confirm.onConfirm(); closeModal(); }}
      />
    </div>
  );
}
